/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useLazyQuery,
} from 'react-apollo';
import type { FC } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Avatar from '@atlaskit/avatar';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import { usePageSpaceKey } from '@confluence/page-context';

import { PressableLink } from '../../../../../../PressableLink.tsx';
import type { SpecificAccessSpaceAdminListQuery as SpecificAccessSpaceAdminListQueryType } from '../graphql/__types__/SpecificAccessSpaceAdminListQuery.ts';

import type {
	SpaceAccessWarningSpecificAccessSpaceAdminListQuery as SpaceAccessWarningSpecificAccessSpaceAdminListQuery$data,
	SpaceAccessWarningSpecificAccessSpaceAdminListQueryVariables as SpaceAccessWarningSpecificAccessSpaceAdminListQuery$variables,
} from './__types__/SpaceAccessWarningSpecificAccessSpaceAdminListQuery.ts';

const SpaceAccessWarningSpecificAccessSpaceAdminListQuery = gql`
	query SpaceAccessWarningSpecificAccessSpaceAdminListQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			admins(accountType: ATLASSIAN) {
				displayName
				profilePicture {
					path
				}
				type
				... on KnownUser {
					accountId
					accountType
					email
				}
			}
			id
		}
	}
`;
const i18n = defineMessages({
	spaceAccessWarningMessage: {
		id: 'share-and-restrict-dialog.principal-details-warning.no-space-permission',
		defaultMessage: 'Needs access to space first. Contact a <spaceAdmin>space admin</spaceAdmin>.',
		description:
			'The warning message in the share and restrict dialog appears when the user does not have space permission. space admin button’s ID: share-and-restrict-dialog.principal-details-warning.space-admin',
	},
	spaceAccessEditWarningMessage: {
		id: 'share-and-restrict-dialog.principal-details-warning.no-space-edit-permission',
		defaultMessage:
			'Needs edit access to space first. Contact a <spaceAdmin>space admin</spaceAdmin>.',
		description:
			'The warning message in the share and restrict dialog appears when the user does not have space edit permission. space admin button’s ID: share-and-restrict-dialog.principal-details-warning.space-admin',
	},
	noSpaceAdminMessage: {
		id: 'share-and-restrict-dialog.principal-details-warning.no-space-admin',
		defaultMessage: 'This space doesn’t currently have a space admin assigned.',
		description: 'A message describing there is no admin assigned to this space.',
	},
});

export const SpaceAccessWarning: FC<{
	accessWarning: 'space-view-access-warning' | 'space-edit-access-warning';
}> = ({ accessWarning }) => {
	const [spaceKey] = usePageSpaceKey();

	const [getSpaceAdmins, { data, loading }] = useLazyQuery<
		SpaceAccessWarningSpecificAccessSpaceAdminListQuery$data,
		SpaceAccessWarningSpecificAccessSpaceAdminListQuery$variables
	>(SpaceAccessWarningSpecificAccessSpaceAdminListQuery);
	const filterForSpaceAdmins = (queryData: SpecificAccessSpaceAdminListQueryType | undefined) => {
		const admins = queryData?.space?.admins || [];

		return admins.filter((admin) => {
			// Anonymous space admins do not have account ids
			return admin && 'accountId' in admin;
		});
	};

	const spaceAdmins = filterForSpaceAdmins(data);

	const handleOpenChange = ({ isOpen }: { isOpen: boolean }) => {
		if (isOpen && spaceKey) {
			getSpaceAdmins({
				variables: { spaceKey },
			});
		}
	};

	const warningMessage =
		accessWarning === 'space-edit-access-warning'
			? i18n.spaceAccessEditWarningMessage
			: i18n.spaceAccessWarningMessage;

	return (
		<DropdownMenu
			placement="bottom"
			shouldFlip={false}
			onOpenChange={({ isOpen }) => handleOpenChange({ isOpen })}
			trigger={({ triggerRef, onClick, ...triggerProps }) => (
				<FormattedMessage
					{...warningMessage}
					values={{
						spaceAdmin: (chunks: React.ReactNode[]) => (
							<Box as="span" ref={triggerRef} {...triggerProps}>
								<PressableLink showUnderline fontSize="small" onClick={onClick}>
									{chunks}
								</PressableLink>
							</Box>
						),
					}}
				/>
			)}
			isLoading={loading}
		>
			<DropdownItemGroup>
				{spaceAdmins.length < 1 ? (
					<DropdownItem isDisabled>
						<Text color="color.text">
							<FormattedMessage {...i18n.noSpaceAdminMessage} />
						</Text>
					</DropdownItem>
				) : (
					spaceAdmins.map((admin) => {
						// Anonymous space admins do not have account ids
						if (admin && 'accountId' in admin) {
							return (
								<DropdownItem
									target="_blank"
									key={admin.accountId}
									href={getAtlassianPeopleProfileUrl(admin.accountId)}
									elemBefore={<Avatar src={admin?.profilePicture?.path} size="small" />}
								>
									{admin.displayName}
								</DropdownItem>
							);
						}
					})
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
